<template>
	<div class="project display-flex flex-column flex-justify-space-between pb-5">
		<div class="px-4" v-if="!showSendInviteDialog">
			<div class="mt-4">
				<div class="project-info py-3 px-2 mb-5 display-flex justify-space-between">
					<SIcon class="project-info-icon mt-n1" name="information"></SIcon>
					<div class="ml-2">
						Collaborators have access to all space items. To let teammates use features, click "share" on individual features.
						<a class="project-info-link" :href="ROLES_AND_PERMISSIONS_LINK" target="_blank">Learn more about permissions in Ply</a>
					</div>
				</div>
				<div class="name pb-4">
					<div v-if="canChangeProjectUsers">
						<UserSelectCombobox :comboboxSelection.sync="comboboxSelection">
							<template #right-side-prepend>
								<ProjectRoleSelectorDisplay :showRemove="false" :role.sync="permissionSelection" />
							</template>
						</UserSelectCombobox>
						<Row v-if="showEmailInvitationDescription" class="flex-align-center justify-between mb-2 mt-4">
							<Row class="flex-align-center">
								<SIcon name="email-invitation-chip" class="mr-2" />
								<h5 class="email-invitation-text pt-1">{{ amountOfMembersText }}</h5>
							</Row>
							<RoleSelector :disabledRoles="disabledRoles" :value="inviteUserRole" :onChange="(role) => (inviteUserRole = role)" />
						</Row>
					</div>

					<ExistingCollaboratorsList v-if="!hasSelection" class="flex-1" :user="user" :projectId="projectId" />
					<CollaboratorsPermissionsExplanation :permissionType="permissionSelection" v-else />
				</div>
			</div>
		</div>
		<InvitationConfirmations
			v-if="showSendInviteDialog"
			:canInviteUsers="canInviteUsers"
			:isProject="true"
			:sendingInviteAndShare="isSending"
			:emailsToInvite="emailsToInvite"
			:usersSelectedCount="usersSelectedCount"
			:nonAccountOwnerAllowShare="nonAccountOwnerAllowShare"
			@onBackClicked="showSendInviteDialog = false"
			@onShareClicked="inviteAndCollaborate"
		/>
		<div v-if="!hasSelection" class="sub-title">Workspace admins also have full access to this space</div>
		<div v-else-if="!showSendInviteDialog" class="width-100 flex-centerize flex-column">
			<div class="sub-title mb-3">Collaborators will be notified that you've shared this space with them.</div>
			<PrimaryButton :loading="isLoading" @click="sendCollaborate">Invite</PrimaryButton>
		</div>
	</div>
</template>

<script>
import isString from 'lodash/isString';
import { mapActions, mapGetters } from 'vuex';

import { AccountPermissions, ProjectPermissions } from '@ply-io/ply-front-shared-utilities';

import CollaboratorsPermissionsExplanation from './ExistingCollaboratorsList/CollaboratorsPermissionsExplanation.vue';
import ExistingCollaboratorsList from './ExistingCollaboratorsList/ExistingCollaboratorsList.vue';
import { ROLES_AND_PERMISSIONS_LINK } from '@/components/Billing/billing-service.const';
import RoleSelector from '@/components/ReactComponentsDataProviders/RoleSelector.vue';
import InvitationConfirmations from '@/components/dialogs/ShareModuleDialog/InvitationConfirmations';
import UserSelectCombobox from '@/components/dialogs/ShareModuleDialog/UserSelectCombobox';
import ProjectRoleSelectorDisplay from '@/components/dialogs/projects/settings/tabs/DataRow/ProjectRoleSelectorDisplay.vue';
import { getAccountMembers } from '@/store/modules/account/actions';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { getGroups } from '@/store/modules/accountGroups/actions';
import { updateProject, collaborateNewProject, setProjectShareData } from '@/store/modules/projects/actions';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { AccountRoles } from '@/utils/account-service.const';
import CollaborateProjectService from '@/utils/collaborate-project-service';
import { pluralize } from '@/utils/language-service';
export default {
	name: 'CollaboratorsTab',
	props: {
		project: { type: Object },
	},
	components: {
		UserSelectCombobox,
		InvitationConfirmations,
		ExistingCollaboratorsList,
		CollaboratorsPermissionsExplanation,
		ProjectRoleSelectorDisplay,
		RoleSelector,
	},
	computed: {
		...mapGetters(['user']),
		...mapGettersShorthand({ hasAccountPermission, projectHasPermission }),
		projectId() {
			return this.project?.id;
		},
		disabledRoles() {
			return [AccountRoles.LiteEndUser, AccountRoles.PremiumEndUser, AccountRoles.StandardEndUser];
		},
		usersInvitedByMail() {
			return this.comboboxSelection.filter((selection) => typeof selection === 'string' || selection.suggestion);
		},
		showEmailInvitationDescription() {
			return this.usersInvitedByMail.length > 0;
		},
		amountOfMembersText() {
			return `${pluralize('member', this.usersInvitedByMail.length, true)} will be invited to Ply, choose their workspace role:`;
		},
		isDataChanged() {
			if (!this.project) {
				return false;
			}
			return this.projectName !== this.project.title || this.description !== this.project.description;
		},
		emailsToInvite() {
			return this.comboboxSelection.filter((u) => isString(u));
		},
		isEmailInvitesExists() {
			return this.emailsToInvite.length > 0;
		},
		existingUsersOrGroupsSelected() {
			return this.comboboxSelection.filter((u) => !isString(u));
		},
		usersSelected() {
			return this.existingUsersOrGroupsSelected.filter((x) => !x.isGroup);
		},
		usersSelectedCount() {
			return this.usersSelected?.length || 0;
		},
		groupsSelected() {
			const usersToShareWith = this.existingUsersOrGroupsSelected;
			return usersToShareWith.filter((x) => x.isGroup).map((x) => x.id);
		},
		allUsersToCollaborateWith() {
			const emailsToInvite = this.canInviteUsers ? this.emailsToInvite.map((e) => ({ email: e })) : [];
			return [...this.usersSelected, ...emailsToInvite];
		},
		nonAccountOwnerAllowShare() {
			return this.isSending || this.usersSelectedCount === 0;
		},
		hasSelection() {
			return this.comboboxSelection.length > 0;
		},
		canChangeProjectUsers() {
			return this.projectHasPermission(this.project?.id, ProjectPermissions.ChangeProjectUsers);
		},
		canCollaborate() {
			return this.hasAccountPermission(AccountPermissions.CollaborateProjects);
		},
		canInviteUsers() {
			return this.hasAccountPermission(AccountPermissions.InviteUsers);
		},
		canViewGroups() {
			return this.hasAccountPermission(AccountPermissions.ViewGroups);
		},
	},
	methods: {
		...mapActions([
			updateProject.fullPath,
			collaborateNewProject.fullPath,
			getAccountMembers.fullPath,
			getGroups.fullPath,
			setProjectShareData.fullPath,
		]),
		async updateProject() {
			const body = {
				...this.project,
				title: this.projectName,
				description: this.projectDescription || '',
			};
			try {
				await this[updateProject.fullPath]({ project: body });
			} catch (err) {
				console.log(err);
				this.$toast.error('Failed updating space');
			}
		},
		async sendCollaborate() {
			if (this.isEmailInvitesExists) {
				this.showSendInviteDialog = true;
			} else {
				this.isLoading = true;
				await this.inviteAndCollaborate();
				this.isLoading = false;
			}
		},
		async inviteAndCollaborate() {
			try {
				const { projectId, emailMessage, isSendMail, groupsSelected, allUsersToCollaborateWith, permissionSelection, inviteUserRole } =
					this;
				this.isSending = true;

				if (
					await CollaborateProjectService.inviteAndCollaborate({
						collaborateNewProjectAction: this[collaborateNewProject.fullPath],
						projectId,
						emailMessage,
						isSendMail,
						groupsSelected,
						allUsersToCollaborateWith,
						role: permissionSelection,
						inviteUserRole,
					})
				) {
					this.comboboxSelection = [];
					this.$toast.success('Successfully added collaborators');
				}
			} catch (err) {
				console.log(err);
				this.$toast.error('We had an issue while collaborating');
			} finally {
				this.isSending = false;
				this.showSendInviteDialog = false;
			}
		},
	},
	async mounted() {
		await this[getAccountMembers.fullPath]();
		await this[setProjectShareData.fullPath]({ id: this.projectId, force: true });
		if (this.canViewGroups) {
			try {
				await this[getGroups.fullPath]();
			} catch (err) {
				this.$toast.error('Failed getting all groups');
			}
		}

		this.projectName = this.project?.title;
		this.projectDescription = this.project?.description;
	},
	data: () => ({
		projectName: '',
		namePlaceholder: 'CDR Catalyst',
		projectDescription: '',
		descriptionPlaceholder: 'Features to delight prospects and supercharge SDRs, from reach-out to hand-off',
		comboboxSelection: [],
		isSending: false,
		showSendInviteDialog: false,
		isLoading: false,
		permissionSelection: '',
		inviteUserRole: AccountRoles.LimitedCreator,
		ROLES_AND_PERMISSIONS_LINK,
	}),
};
</script>

<style scoped lang="scss">
.project {
	.sub-title {
		font-size: 12px;
		color: #9e9e9e;
	}
	.add-description {
		text-decoration: underline;
		color: #9e9e9e;
		font-size: 14px;
		cursor: pointer;
	}
	.project-info {
		background: #e5e5e5;
		border-radius: 5px;
		font-weight: 400;
		font-size: 14px;
		line-height: 18px;
		color: #4d4c52;
		.project-info-link {
			color: #4d4c52;
		}

		.project-info-icon {
			font-size: 8px;
			color: black !important;
		}
	}
}
.email-invitation-text {
	font-size: 11px;
	letter-spacing: 0px;
	color: var(--primary-font-color);
}
</style>
